// Accordion behavior for the Access Report provider directory's EIN-grouped
// rows, shared by the access-report Stimulus controller and its node:test suite.
//
// The server renders both states — the representative row and its collapsed
// sibling rows (`tr[data-group-child="<npi>"]`, starting `hidden`) — in the same
// response, so expanding is purely a class toggle. Nothing here fetches.
// See docs/access_report.md.

export const CHILD_ROW_SELECTOR = "tr[data-group-child]"

function childRowsFor(root, groupId) {
  return Array.from(root.querySelectorAll(`tr[data-group-child="${groupId}"]`))
}

function setChevron(toggle, expanded) {
  if (toggle && toggle.setAttribute) toggle.setAttribute("aria-expanded", String(expanded))
}

// Toggles one group's sibling rows and returns whether it is now expanded.
// A group with no rendered children is a no-op (false), not a crash.
export function toggleProviderGroup(root, toggle) {
  const groupId = toggle && toggle.dataset ? toggle.dataset.groupId : null
  if (!groupId) return false

  const children = childRowsFor(root, groupId)
  if (children.length === 0) return false

  const expanded = children[0].classList.contains("hidden")
  children.forEach((row) => row.classList.toggle("hidden", !expanded))
  setChevron(toggle, expanded)

  return expanded
}

// Collapses every group in the table. Used when the directory is filtered down
// to one provider (and when that filter clears): sibling rows carry no
// `data-npi`, so the filter's own show/hide pass never sees them and an
// expanded group would otherwise survive the filter as orphaned rows.
//
// That same blind spot applies to highlighting, so this clears it here too:
// the filter's pass drops `highlighted` from every `tr[data-npi]` it walks and
// would leave a sibling marked from the previous selection lit forever.
export function collapseAllProviderGroups(root) {
  root.querySelectorAll(CHILD_ROW_SELECTOR).forEach((row) => {
    row.classList.add("hidden")
    row.classList.remove("highlighted")
  })
  root.querySelectorAll("[data-group-id]").forEach((toggle) => {
    if (toggle.setAttribute) toggle.setAttribute("aria-expanded", "false")
  })
}

// Locates a sibling NPI that the directory only renders as a collapsed child
// row, returning `{ row, groupId }` (groupId being the parent row's npi) or
// null when this npi isn't one. The provider-search dropdown queries NPPES
// directly, so it can offer an NPI that has no `tr[data-npi]` row of its own —
// without this, selecting it would match nothing and silently do nothing.
export function findGroupChild(root, npi) {
  if (!npi || !root || typeof root.querySelector !== "function") return null

  const row = root.querySelector(`tr[data-child-npi="${npi}"]`)
  const groupId = row && row.dataset ? row.dataset.groupChild : null

  return groupId ? { row, groupId } : null
}

// Expands one group unconditionally (as opposed to toggling it), so a caller
// that has just filtered the table — which collapses every group — can reopen
// the one holding the sibling the user asked for. Returns whether it opened
// anything.
export function expandProviderGroup(root, groupId) {
  if (!groupId) return false

  const children = childRowsFor(root, groupId)
  if (children.length === 0) return false

  children.forEach((row) => row.classList.remove("hidden"))
  setChevron(root.querySelector(`[data-group-id="${groupId}"]`), true)

  return true
}
